import { FormEvent, ReactNode, useEffect, useState } from "react";
import PriceSelector from "../PriceSelector";
import { Button, Col, Form, Input, Label, Row, Table } from "reactstrap";
import styles from "./style.module.scss";
import { FontAwesomeIcon } from "@fortawesome/react-fontawesome";
import { faCheck, faMagnifyingGlass, faPlus, faXmark } from "@fortawesome/free-solid-svg-icons";
import LoadingScreen from "@components/content/LoadingScreen";
import { ProductDataType } from "@/api/ProductsApi";
import { InvoiceTemplateApi, InvoiceTemplateDataType } from "@/api/InvoiceTemplateApi";
import { showAlert } from "@utils/Alert";

type InvoiceTemplateSearchProps = {
  onTemplateSelect: (invoiceTemplate: InvoiceTemplateDataType) => void
};

const MAX_ITEMS_PER_PAGE = 20;

const InvoiceTemplateSearch = (props: InvoiceTemplateSearchProps) => {
  const { onTemplateSelect } = props;
  const [invoiceTemplates, setInvoiceTemplates] = useState<InvoiceTemplateDataType[]>([]);
  const [isLoading, setIsLoading] = useState<boolean>(false);
  const [nextPageToken, setNextPageToken] = useState<string>("");
  const [inputValue, setInputValue] = useState<string>("");
  const [filterText, setFilterText] = useState<string | boolean>(false);
  const [hasMorePages, setHasMorePages] = useState(false);

  useEffect(() => {
    setTimeout(() => {
      performSearch("")
    });
  }, [])

  useEffect(() => {
    if (filterText !== false) {
      performSearch(`${filterText}`)
    }
  }, [filterText]);

  const performSearch = async (text: string) => {
    const templates = await fetchInvoiceTemplates(text);
    setInvoiceTemplates(templates);
  }

  const fetchInvoiceTemplates = async (filterText: string, token: string = ''): Promise<ProductDataType[]> => {
    setIsLoading(true)
    try {
      const { nextToken, items } = await InvoiceTemplateApi.fetchAll(generateFilterCriteria(`${filterText}`), token, MAX_ITEMS_PER_PAGE);
      setHasMorePages(!!nextToken);
      setNextPageToken(nextToken || "");
      return items;
    } finally {
      setIsLoading(false)
    }
  }

  const search = (e: FormEvent) => {
    e.preventDefault();
    setFilterText(inputValue);
  }

  const generateFilterCriteria = (filterText: string) => {
    const productFilterObject = [
      { title: { contains: filterText } },
      { codigo: { eq: filterText } },
    ]

    return productFilterObject.length ? {
      or: productFilterObject
    } : []
  }

  const clearSearchForm = () => {
    setInputValue("");
    setFilterText("");
  }

  const renderFilterForm = (): ReactNode | null => {
    return (
      <>
        <Form onSubmit={(e) => search(e)}>
          <Row className="row-cols-lg-auto g-1 align-items-center">
            <Col>
              <Label className="g-0 pe-3"
                for="general-search-criteria"
              >
                Filtro
              </Label>
            </Col>
            <Col lg="3">
              <Input
                id="general-search-criteria"
                name="filter"
                type="text"
                value={inputValue}
                onChange={(e) => setInputValue(e.target.value)}
              />
            </Col>
            <Col>
              <Button className="me-1" color="primary" type="submit">
                <FontAwesomeIcon icon={faMagnifyingGlass} />
              </Button>
              <Button color="danger" type="button" onClick={() => clearSearchForm()}>
                <FontAwesomeIcon icon={faXmark} />
              </Button>
            </Col>
          </Row>
        </Form>
        <hr />
      </>
    )
  }

  const renderProduct = (): ReactNode[] => {
    return invoiceTemplates.map(t => {
      return (
        <tr>
          <td>
            {t.numero}
          </td>
          <td className="col-5">
            {t.title}
          </td>
          <td className="col-2">
            <Button type="button" onClick={() => { onTemplateSelect(t) }} color="primary">
              <FontAwesomeIcon icon={faCheck} /> Seleccionar
            </Button>
          </td>
        </tr>
      )
    })
  }

  const loadMoreItems = async () => {
    const items = await fetchInvoiceTemplates(`${filterText}`, nextPageToken);
    setInvoiceTemplates([
      ...invoiceTemplates,
      ...items]
    );
  }

  return (
    <>
      <Row>
        <Col>
          {renderFilterForm()}
        </Col>
        <Col xs="1">
          <PriceSelector />
        </Col>
      </Row>
      <Table className={styles.entrysTable} size="sm">
        <thead>
          <tr>
            <th>
              Codigo
            </th>
            <th>
              Titulo
            </th>
            <th>
              Seleccionar
            </th>
          </tr>
        </thead>
        <tbody>
          {renderProduct()}
        </tbody>
      </Table>
      {hasMorePages && (
        <div className={styles.loadMoreButton}>
          <Button type="button" color="primary" onClick={loadMoreItems}>Cargar Mas</Button>
        </div>
      )}

      <LoadingScreen isLoading={isLoading} />
    </>
  )
}

export default InvoiceTemplateSearch;